<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { TheUploader, VAlert, VButton, VLoadingSection, VModal, VSection } from '@/modules/shared/components'
import type { CloseReadiness } from '@/modules/investing/types'

const props = defineProps<{
  modalStore: { state: { active: boolean }; open: () => void; close: () => void }
  entityType: 'spv' | 'fund'
  // Called each time the slide-out opens. Fetched per-open so a portfolio
  // investment recorded between opens flips the flow from prompt to form.
  fetchCloseReadiness: () => Promise<CloseReadiness>
  // Rails-side URL for recording the portfolio investment. Same href used by
  // the raising overview's "Complete Now" CTA in closing-progress.vue.
  // Null when the SPV's deal card has no company yet — in that case the CTA
  // is hidden.
  completeInvestmentHref: string | null
  onSubmit: (payload: { closing_documents: File[] }) => Promise<void>
}>()

const entityLabel = computed(() => (props.entityType === 'fund' ? 'Fund' : 'SPV'))

type Step = 'loading' | 'prompt' | 'form'

const initialForm = () => ({
  closing_documents: [] as File[],
})

const step = ref<Step>('loading')
const form = ref(initialForm())
const submitting = ref(false)
const error = ref<string | null>(null)

watch(
  () => props.modalStore.state.active,
  async (active) => {
    if (!active) {
      form.value = initialForm()
      submitting.value = false
      error.value = null
      return
    }

    // Modal opens immediately in the loading state; readiness is informational.
    // If the fetch fails, fall through to the form — the prompt is a UX
    // safeguard, not an authoritative server guard.
    step.value = 'loading'
    try {
      const readiness = await props.fetchCloseReadiness()
      step.value = readiness.has_portfolio_investment ? 'form' : 'prompt'
    } catch {
      step.value = 'form'
    }
  },
)

const cancel = () => props.modalStore.close()

const addInvestment = () => {
  if (!props.completeInvestmentHref) return
  props.modalStore.close()
  window.location.href = props.completeInvestmentHref
}

const submit = async () => {
  if (submitting.value) return

  submitting.value = true
  error.value = null
  try {
    await props.onSubmit({
      closing_documents: form.value.closing_documents,
    })
    props.modalStore.close()
  } catch (e: any) {
    error.value = e?.data?.error ?? e?.message ?? `Failed to close ${entityLabel.value}`
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <VModal :modalStore="modalStore">
    <template #main>
      <div v-if="step === 'loading'" class="flex h-64 items-center justify-center">
        <VLoadingSection description="Checking close requirements…" />
      </div>

      <VSection v-else-if="step === 'prompt'" label="Before you close">
        <p class="text-sm leading-relaxed text-gray-600">
          This {{ entityLabel }} doesn't have a portfolio investment recorded yet. Closing now would lock the
          {{ entityLabel }} with no holdings to track. Add the investment first, then come back to close.
        </p>

        <VAlert type="info" class="mt-6">
          A portfolio investment captures what this {{ entityLabel }} actually invested in — the company, terms, and
          amount. It's the source of truth for distributions, valuations, and exit reporting later on.
        </VAlert>

        <div v-if="completeInvestmentHref" class="mt-6 space-y-3">
          <button
            type="button"
            class="group block w-full rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-sky-300 hover:bg-sky-50/40"
            @click="addInvestment"
          >
            <div class="font-medium text-gray-800 group-hover:text-sky-900">Add an investment</div>
            <div class="mt-1 text-sm text-gray-500">
              Record the company, terms, and amount this {{ entityLabel }} invested before closing it out.
            </div>
          </button>
        </div>
      </VSection>

      <VSection v-else :label="`Close this ${entityLabel}?`">
        <p class="text-sm leading-relaxed text-gray-600">
          Closing locks the {{ entityLabel }} — no new investors can commit while it is closed. You can attach closing
          documents now, or upload them later from the {{ entityLabel }}'s files.
        </p>

        <form class="mt-6 space-y-6" @submit.prevent="submit">
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700"
              >Closing documents <span class="font-normal text-gray-400">(optional)</span></label
            >
            <TheUploader :accept="[]" @chosen="(files: File[]) => (form.closing_documents = files)" />
          </div>

          <p v-if="error" class="text-sm text-red-600" role="alert">{{ error }}</p>
        </form>
      </VSection>
    </template>

    <template #footer>
      <div v-if="step === 'loading'" class="flex items-center justify-between gap-3">
        <VButton :click="cancel" size="lg" class="w-full">Cancel</VButton>
      </div>
      <div v-else-if="step === 'prompt'" class="flex items-center justify-between gap-3">
        <VButton :click="cancel" size="lg" class="w-full">Not now</VButton>
      </div>
      <div v-else class="flex items-center justify-between gap-3">
        <VButton :click="cancel" :disabled="submitting" size="lg">Cancel</VButton>
        <VButton :click="submit" :loading="submitting" size="lg" variant="primary" class="w-full"
          >Close {{ entityLabel }}</VButton
        >
      </div>
    </template>
  </VModal>
</template>
